import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { DynamicFormField } from './components/DynamicFormField';
import { PV04FormSchema, PV04FormField } from './components/types';
import './pv04.css';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useMediaQuery } from 'react-responsive';
interface PV04FormProps {
  openPrevious: () => void;
  onSubmit: (e?: any, data?: Record<string, string>) => void;
  requireInfoData: PV04FormSchema | null;
  isGlobal: boolean;
}

const PV04Form: React.FC<PV04FormProps> = ({
  openPrevious,
  onSubmit,
  requireInfoData,
  isGlobal,
}) => {
  // 폼 데이터 상태
  const [formData, setFormData] = useState<Record<string, string>>({});

  // 필드 가시성 상태
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>(
    {},
  );
  const isMobileSize = useMediaQuery({ maxWidth: 767 });

  const { t } = useTranslation();

  function removeSelectOptionsWithSingleItem(data: any[], setFormData) {
    const filteredData: any[] = [];
    const removedItems: any[] = [];

    data.forEach(item => {
      if (
        item.renderType.startsWith('select_') &&
        item.selectOption &&
        item.selectOption.length === 1
      ) {
        // 제거된 객체 저장
        removedItems.push(item);

        // setFormData 호출하여 자동으로 값 설정
        const name = item.name;
        const value = item.selectOption[0].value;

        setFormData(prevData => {
          const newFormData = {
            ...prevData,
            [name]: value,
          };
          return newFormData;
        });
      } else {
        // 조건에 맞지 않는 객체는 유지
        filteredData.push(item);
      }
    });

    return {
      filteredData,
      removedItems,
    };
  }
  // 정렬된 필드 목록
  const sortedFields = useMemo(() => {
    if (!requireInfoData?.fields) return [];

    return removeSelectOptionsWithSingleItem(
      requireInfoData?.fields,
      setFormData,
    ).filteredData;

    // return [...requireInfoData.fields].sort((a, b) => a.order - b.order);
    // return requireInfoData?.fields;
  }, [requireInfoData?.fields]);

  const validateField = useCallback(
    (field: PV04FormField, value: string): boolean => {
      // required 체크
      if (!value.trim()) return false;

      const validMaxLength = Number(field.length);
      const fieldName = field.name;

      if (fieldName === 'email') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }

      if (value.length < validMaxLength) return true;

      return true;
    },

    [],
  );

  useEffect(() => {
    if (!requireInfoData?.fields) return;

    const removedItem = removeSelectOptionsWithSingleItem(
      requireInfoData?.fields,
      setFormData,
    ).removedItems;

    const initialData: Record<string, string> = {};

    try {
      removedItem.forEach(item => {
        initialData[item.name] = item['selectOption'][0].value;
      });
    } catch (e) {}
    requireInfoData.fields.forEach((field: PV04FormField) => {
      // 초기값 저장되어있는경우 참고
      // initialData[field.name] = '';
      initialData[field.name] = initialData[field.name] || '';
    });

    setFormData(prevData => {
      // 데이터가 실제로 변경된 경우에만 업데이트
      const hasChanged =
        Object.keys(initialData).some(
          key => prevData[key] !== initialData[key],
        ) || Object.keys(prevData).length !== Object.keys(initialData).length;

      if (!hasChanged) return prevData;

      return initialData;
    });
  }, [requireInfoData?.fields]);

  // 단계적 필드 표시 로직
  useEffect(() => {
    if (!sortedFields.length) return;

    const newVisibleFields: Record<string, boolean> = {};
    let shouldShowNext = true;

    for (const field of sortedFields) {
      if (shouldShowNext) {
        newVisibleFields[field.name] = true;

        const currentValue = formData[field.name] ?? '';
        if (!validateField(field, currentValue)) {
          shouldShowNext = false;
        }
      } else {
        newVisibleFields[field.name] = false;
      }
    }
    setVisibleFields(newVisibleFields);
  }, [formData, sortedFields, validateField]);

  // 필드 값 변경 핸들러
  const handleFieldChange = useCallback((name: string, value: string) => {
    setFormData(prevData => {
      const newFormData = {
        ...prevData,
        [name]: value,
      };

      return newFormData;
    });
  }, []);

  // 폼 완성 여부 확인
  const isFormComplete = useMemo(() => {
    if (!sortedFields.length) return false;

    return sortedFields.every((field: PV04FormField) =>
      validateField(field, formData[field.name] ?? ''),
    );
  }, [formData, sortedFields, validateField]);

  return (
    <>
      <div className="tab">
        <div
          id="tab-3"
          className="tab-on current"
          style={{
            height: isGlobal
              ? isMobileSize
                ? 'calc((var(--vh, 1vh) * 100) - 324px)'
                : 274
              : isMobileSize
              ? 'calc((var(--vh, 1vh) * 100) - 258px)'
              : 340,
          }}
        >
          <div className="form first">
            {sortedFields.map((field: PV04FormField) => (
              <DynamicFormField
                key={field.name}
                schema={field}
                value={formData[field.name] ?? ''}
                onChange={handleFieldChange}
                isVisible={visibleFields[field.name] || false}
                validateField={validateField}
              />
            ))}
          </div>
        </div>

        <div className="c-section">
          <div className="box">
            <button className="all-close" onClick={openPrevious}>
              {t('previous')}
            </button>
            <button
              className={`orange ${isFormComplete ? 'enabled' : 'disabled'}`}
              onClick={e => onSubmit(e, formData)}
              disabled={!isFormComplete}
            >
              {t('pay_now')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PV04Form;
